import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Heading from "@components/layout/Heading";
import Table from "@components/layout/Table";
import ExternalLinkIcon from "@components/icons/ExternalLinkIcon";
import AccountingPeriodChecklistFooter from "./AccountingPeriodChecklistFooter";
import { useAccountingPeriodChecksContext } from "@context/AccountingPeriodChecksContext";
import { useAllExtractionRights } from "@hooks/query/useAllExtractionRights";
import { extractionRightTypes } from "@services/extractionRight";
import { useAppContext } from "@context/AppContext";
import { formatVolume } from "@utils/formatVolume";
import { formatDate } from "@utils/formatDate";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import SelectLevel0Resource from "@components/form/SelectLevel0Resource";

type Filter = {
  subscriberNameOrAccountNumber: string;
  extractionRightName: string;
  level0ResourceIdentifier: string;
};

const StockAndDomesticsChecklist: React.FunctionComponent = () => {
  const { loggedInInfo } = useAppContext();
  const {
    currentAccountingPeriodCheck,
    carriedOverSDIds,
    setCarriedOverSDIds,
  } = useAccountingPeriodChecksContext();
  const { t } = useTranslation();

  const [filter, setFilter] = React.useState<Filter>({
    subscriberNameOrAccountNumber: "",
    extractionRightName: "",
    level0ResourceIdentifier: "",
  });

  const level1ResourceId =
    currentAccountingPeriodCheck?.accountingPeriod?.level1ResourceId;

  const { data: sdRights = [], isLoading } = useAllExtractionRights({
    params: {
      level1ResourceId,
      types: [extractionRightTypes.SD],
    },
    options: {
      enabled: Boolean(level1ResourceId),
      onSuccess: (data: any[]) => {
        if (data.length === 0 || carriedOverSDIds.length > 0) return;

        setCarriedOverSDIds(
          data
            .filter(i => i.carriedOverFromAccountingPeriodCheckId)
            .map(i => i.id),
        );
      },
    },
  });

  const handleFilterChange = <T extends keyof Filter>(
    field: T,
    value: Filter[T],
  ) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const tableFields = [
    {
      title: t("subscriber.account_number"),
      name: "subscriberAccountNumber",
    },
    {
      title: t("subscriber.name"),
      name: "subscriberName",
    },
    {
      title: t("extraction_right.number"),
      name: "name",
    },
    {
      title: t("common.level0wrs"),
      name: "level0Resource",
    },
    {
      title: t("common.volume"),
      name: "volume",
    },
    {
      title: t("extraction_right.start_at"),
      name: "startAt",
    },
    {
      title: "",
      name: "actions",
    },
  ];

  const tableData = sdRights
    ?.filter(
      (i: any) =>
        !filter.subscriberNameOrAccountNumber.length ||
        i?.subscriber?.name
          .toLowerCase()
          .includes(filter.subscriberNameOrAccountNumber.toLowerCase()) ||
        i?.subscriber?.accountNumber
          .toString()
          .includes(filter.subscriberNameOrAccountNumber),
    )
    ?.filter(
      (i: any) =>
        !filter.extractionRightName.length ||
        i?.name
          .toLowerCase()
          .includes(filter.extractionRightName.toLowerCase()),
    )
    ?.filter(
      (i: any) =>
        !filter.level0ResourceIdentifier ||
        i?.level0Resource?.identifier.includes(filter.level0ResourceIdentifier),
    )
    ?.map((i: any) => {
      return {
        ...i,
        subscriberAccountNumber: i.subscriber?.accountNumber,
        subscriberName: i.subscriber?.name,
        level0Resource: i.level0Resource?.identifier,
        volume: formatVolume(i.volume),
        startAt: formatDate(new Date(i.startAt)),
        actions: (
          <div className="inline-block">
            <Link
              className="btn-default text-sm flex items-center gap-1"
              to={`/polestar/subscribers/${i.subscriber.id}/level0_resources/${i.level0ResourceId}/extraction_rights?name=${i.name}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLinkIcon className="w-3 h-3" />
              {t("common.view")}
            </Link>
          </div>
        ),
      };
    });

  return (
    <>
      <div className="flex flex-col grow gap-4 p-4">
        <header className="flex justify-between items-center">
          <Heading light>
            {t("accounting_period.checklist.stock_and_domestics")}
          </Heading>
        </header>

        <form className="flex flex-wrap gap-3 justify-start">
          <div>
            <Label htmlFor="subscriber">
              {t("subscriber.filter.account_number_or_name")}
            </Label>
            <SearchInput
              id="subscriber"
              value={filter.subscriberNameOrAccountNumber}
              onChange={e =>
                handleFilterChange(
                  "subscriberNameOrAccountNumber",
                  e.target.value,
                )
              }
            />
          </div>
          <div>
            <Label htmlFor="extraction_right_name">
              {t("extraction_right.filter_extraction_right_number")}
            </Label>
            <SearchInput
              id="extraction_right_name"
              value={filter.extractionRightName}
              onChange={e =>
                handleFilterChange("extractionRightName", e.target.value)
              }
            />
          </div>
          <div>
            <Label htmlFor="level0_resource">
              {t("subscriber.filter.level0wrs")}
            </Label>
            <SelectLevel0Resource
              inputId="level0_resource"
              level1ResourceId={level1ResourceId}
              value={filter.level0ResourceIdentifier}
              onChange={(e: any) => {
                handleFilterChange("level0ResourceIdentifier", e?.label || "");
              }}
              isDisabled={!Boolean(level1ResourceId)}
              isClearable
            />
          </div>

          <div className="flex items-end w-full ml-auto sm:w-auto">
            <Link
              className="btn-secondary flex items-center gap-1"
              to="/polestar/administrative_approvals/stock_and_domestic?newWindow=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLinkIcon className="w-4 h-4" />
              {t("approval.stock_and_domestic.add_new")}
            </Link>
          </div>
        </form>

        <Table
          fields={tableFields}
          data={tableData}
          loading={isLoading}
          selectionKey="id"
          selectedKeys={carriedOverSDIds}
          onSelectionChange={setCarriedOverSDIds}
          stickyHeader
        />
      </div>
      <AccountingPeriodChecklistFooter
        stepData={{
          stockAndDomesticsCheckedAt: new Date(),
          stockAndDomesticsCheckedByUserId: loggedInInfo?.userDetails?.id,
          carriedOverSDIds,
          deletedSDIds: sdRights
            .filter((i: any) => i?.carriedOverFromAccountingPeriodCheckId)
            .filter((i: any) => !carriedOverSDIds.includes(i.id))
            .map((i: any) => i.id),
        }}
      />
    </>
  );
};

export default StockAndDomesticsChecklist;
